import { useEffect, useState } from 'react';
import styled from 'styled-components';
// eslint-disable-next-line no-restricted-imports
import { showToastAsync } from '../../../data-layer/notifications/actions';
// eslint-disable-next-line no-restricted-imports
import { useDispatch } from '../../../data-layer/useDispatch';
import { getWindow } from '../../../utils/getWindow';
import { useTranslation } from '../../../utils/useTranslation';
import { DataTestId } from '../../layout/DataTestIdWrapper';
import { Flex } from '../../layout/Flex';
import { Typography } from '../../typography/Typography';
import { PostAILabel } from '../PostAILabel/PostAILabel';
import { PostUI, ReactionState } from '../types';
import { POST_GRID_CARD_FOOTER_HEIGHT } from './constants';

const ItemContainer = styled(Flex)
    .withConfig({ displayName: 'ItemContainer' })
    .attrs((props) => ({
        ...props,
        height: 48,
        px: 2,
        width: '100%',
        cursor: 'pointer',
    }))`
    justify-content: center;
    transition: background-color 0.2s ease-in-out;

    &:hover {
        background-color: var(--background-secondary);
    }
`;

const Container = styled(Flex)
    .withConfig({ displayName: 'Container' })
    .attrs((props) => ({
        ...props,
        backgroundColor: 'var(--background-primary)',
        position: 'absolute',
        right: 0.5,
        bottom: `calc(${POST_GRID_CARD_FOOTER_HEIGHT}px + 5px)`,
        borderRadius: 1,
        overflow: 'hidden',
        boxShadow: '0 8px 20px 0 var(--placeholder)',
    }))``;

export type PostGridCardKebabProps = {
    onReport?: VoidFunction;
    onShare?: (p: { mediaId: string }) => void;
    onBusinessPage?: (isKebab: boolean) => void;
    onShareCollection?: VoidFunction;
    onSaveCollection?: VoidFunction;
    onRemoveSavedCollection?: VoidFunction;
    onReaction: (params: {
        postNumber: number;
        reactionState: PostUI['reactionState'];
        preventReactionToggle?: boolean;
    }) => void;
    post: PostUI;
    shareDisabled?: boolean;
    businessPageDisabled?: boolean;
};

export const usePostGridCardKebab = ({
    onReport,
    onShare,
    onBusinessPage,
    onShareCollection,
    onSaveCollection,
    onRemoveSavedCollection,
    onReaction,
    post,
    shareDisabled,
    businessPageDisabled,
}: PostGridCardKebabProps) => {
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const t = useTranslation();

    useEffect(() => {
        const handleClick = () => {
            setIsOpen(false);
        };

        getWindow()?.document.addEventListener('click', handleClick);
        getWindow()?.document.addEventListener('touchstart', handleClick);

        return () => {
            getWindow()?.document.removeEventListener('click', handleClick);
            getWindow()?.document.removeEventListener('touchstart', handleClick);
        };
    }, []);

    const isCollection = post.postType === 'collection';
    const isSaved = post.collectionData?.isSaved;
    const isOwner = post.ownersData?.isOwner;

    return {
        kebab: () => {
            if (!isOpen) {
                return null;
            }

            return (
                <Container
                    onTouchStart={(e: React.TouchEvent) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }}
                >
                    <Flex
                        borderRadius={1}
                        backgroundColor="var(--background-primary)"
                        width="100%"
                        zIndex={1}
                        dataTestId={DataTestId.POST_CONTEXT_MENU}
                    >
                        {!isCollection && !businessPageDisabled && onBusinessPage ? (
                            <ItemContainer
                                dataTestId={DataTestId.GO_TO_PROFILE_TEXT}
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    onBusinessPage?.(true);
                                    setIsOpen(false);
                                }}
                            >
                                <Typography variant="body" weight="regular">
                                    {t('Перейти к профилю')}
                                </Typography>
                            </ItemContainer>
                        ) : null}
                        {!shareDisabled && (
                            <ItemContainer
                                dataTestId={DataTestId.SHARE_TEXT}
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    !isCollection && onShare?.({ mediaId: post.content[0].id });
                                    isCollection && onShareCollection?.();
                                    setIsOpen(false);
                                }}
                            >
                                <Typography variant="body" weight="regular">
                                    {t('Поделиться')}
                                </Typography>
                            </ItemContainer>
                        )}
                        {isCollection && !isOwner ? (
                            <ItemContainer
                                dataTestId={
                                    isSaved
                                        ? DataTestId.REMOVE_SAVED_COLLECTION
                                        : DataTestId.SAVE_COLLECTION
                                }
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    isSaved && onRemoveSavedCollection?.();
                                    !isSaved && onSaveCollection?.();
                                    setIsOpen(false);
                                }}
                            >
                                <Typography variant="body" weight="regular">
                                    {t(isSaved ? 'Убрать из сохраненных' : 'Сохранить')}
                                </Typography>
                            </ItemContainer>
                        ) : null}
                        <ItemContainer
                            dataTestId={DataTestId.DISLIKE_TEXT}
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onReaction({
                                    postNumber: 1,
                                    reactionState: ReactionState.DISLIKE,
                                    preventReactionToggle: true,
                                });
                                setIsOpen(false);
                                dispatch(
                                    showToastAsync({
                                        message: 'Спасибо, такой контент будет показываться реже',
                                    }),
                                );
                            }}
                        >
                            <Typography variant="body" weight="regular">
                                {t('Меньше такого')}
                            </Typography>
                        </ItemContainer>
                        {!isOwner && (
                            <ItemContainer
                                dataTestId={DataTestId.REPORT_TEXT}
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    onReport?.();
                                    setIsOpen(false);
                                }}
                            >
                                <Typography variant="body" weight="regular">
                                    {t('Пожаловаться')}
                                </Typography>
                            </ItemContainer>
                        )}
                    </Flex>
                    {post.isAIContent ? (
                        <PostAILabel
                            p={2}
                            pt={3}
                            mt={-1}
                            background="var(--background-secondary)"
                            text={t('Контент, созданный с помощью ИИ')}
                        />
                    ) : null}
                </Container>
            );
        },
        openKebab: (e: React.MouseEvent) => {
            e.stopPropagation();
            isOpen ? setIsOpen(false) : setIsOpen(true);
        },
    };
};
